import { useState, useEffect, useRef } from 'react';
import Map, { Marker, NavigationControl } from 'react-map-gl/maplibre';
import maplibregl, { StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { FEATURE, hasHiddenFeatureSettings, omitUnsupportedFields, supportsFeature } from './modelFeatures';
import ButtonAssignmentsEditor from './ButtonAssignmentsEditor';
import { DmrBox } from './DmrDiagnostics';
import PocSwitch from './PocSwitch';
import WifiControls from './WifiControls';
import { wifiResultState } from './wifiNetworkText';
import RadioDmrSettings from './RadioDmrSettings';
import { assignmentPayload, choicesChanged, initialChoices, type AssignmentChoices } from './buttonAssignments';
import { describeAlarmPosition } from '../../components/alarmPosition';
import { formatGpsFixTime } from './gpsIndicator';
import {
  Radio,
  Group,
  MobileNetworkOperator,
  WifiNetwork,
  SosEvent,
  RadioAvailabilityStatus,
  getRadiosByOrg,
  getAllRadios,
  getGroupsByOrg,
  getRadioAvailabilityStatuses,
  setRadioAvailabilityStatus,
  createRadio,
  updateRadioDetails,
  updateRadioSOSSettings,
  getGroupMembers,
  addMember,
  removeMember,
  getDeviceInfo,
  setDisplayTimeout,
  setMessageRetentionDays,
  setOfflineLocationRetentionDays,
  setMessagesEnabled,
  setStatusEnabled,
  setDmrEnabled,
  setPocEnabled,
  type DmrStatus,
  getRadioButtonAssignments,
  setRadioButtonAssignments,
  type RadioButtonAssignments,
  getUnresolvedSosEvents,
  resolveSosEvent,
  requestMobileNetworkScan,
  requestMobileNetworkSwitch,
  requestMobileNetworkAutomatic,
  requestWifiScan,
  requestWifiConnect,
  blockRadio,
  unblockRadio,
  sendCallAlert,
  sendDirectMessage,
  sendDirectMessageToAll,
} from '../../services/admin';

type SortField = 'radio_id' | 'name' | 'model' | 'status' | 'availability_status_name' | 'priority';
type Priority = 'low' | 'standard' | 'high';
type TTSLanguage = 'da' | 'en';

const PRIORITY_OPTIONS: { value: Priority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'standard', label: 'Standard' },
  { value: 'high', label: 'High' },
];

const TTS_LANGUAGE_OPTIONS: { value: TTSLanguage; label: string }[] = [
  { value: 'da', label: 'Danish' },
  { value: 'en', label: 'English' },
];

const DISPLAY_TIMEOUT_OPTIONS: { ms: number; label: string }[] = [
  { ms: 15000, label: '15 seconds' },
  { ms: 30000, label: '30 seconds' },
  { ms: 60000, label: '1 minute' },
  { ms: 120000, label: '2 minutes' },
  { ms: 300000, label: '5 minutes' },
  { ms: 600000, label: '10 minutes' },
  { ms: 1800000, label: '30 minutes' },
];

const priorityOrder: Record<string, number> = { low: 0, standard: 1, high: 2 };

const lastKnownLocationMapStyle: StyleSpecification = {
  version: 8,
  sources: {
    openstreetmap: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [
    {
      id: 'openstreetmap',
      type: 'raster',
      source: 'openstreetmap',
    },
  ],
};

function formatLastSeen(value: string): string {
  return new Intl.DateTimeFormat('da-DK', {
    dateStyle: 'short',
    timeStyle: 'medium',
    hour12: false,
  }).format(new Date(value));
}

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'high':
      return 'bg-red-500/20 text-red-400 border-red-500';
    case 'low':
      return 'bg-gray-500/20 text-gray-400 border-gray-500';
    default:
      return 'bg-blue-500/20 text-blue-400 border-blue-500';
  }
};

export default function RadiosManager({ orgId }: { orgId: string }) {
  const [radios, setRadios] = useState<Radio[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [availabilityStatuses, setAvailabilityStatuses] = useState<RadioAvailabilityStatus[]>([]);
  const [statusMenuRadioId, setStatusMenuRadioId] = useState<string | null>(null);
  const [androidIds, setAndroidIds] = useState<Record<string, string | null>>({});
  const [activeAlarms, setActiveAlarms] = useState<SosEvent[]>([]);
  const [resolvingAlarmId, setResolvingAlarmId] = useState<string | null>(null);
  const [confirmingResolveAlarmId, setConfirmingResolveAlarmId] = useState<string | null>(null);
  const [blockingId, setBlockingId] = useState<string | null>(null);
  const [callAlertId, setCallAlertId] = useState<string | null>(null);
  const [callAlertRadio, setCallAlertRadio] = useState<Radio | null>(null);
  const [callAlertMessage, setCallAlertMessage] = useState('');
  const [messageRadio, setMessageRadio] = useState<Radio | null>(null);
  const [messageToAll, setMessageToAll] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [displayTimeoutMs, setDisplayTimeoutMs] = useState(60000);
  const [initialDisplayTimeoutMs, setInitialDisplayTimeoutMs] = useState(60000);
  const [displayTimeoutLoaded, setDisplayTimeoutLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [sortField, setSortField] = useState<SortField>('radio_id');
  const [sortAsc, setSortAsc] = useState(true);
  const [formData, setFormData] = useState<Partial<Radio>>({
    radio_id: '',
    model: 'Inrico S380',
    name: '',
    priority: 'standard',
    tts_enabled: true,
    tts_language: 'da',
    ptt_tone_enabled: true,
    busy_tone_enabled: true,
    connection_tts_enabled: false,
    incoming_ptt_start_enabled: true,
    incoming_ptt_stop_enabled: true,
    sos_enabled: true,
    sos_alarm_sound_enabled: true,
    sos_visual_enabled: true,
    sos_recipient_mode: 'radio',
    low_battery_threshold_percent: 10,
    low_battery_tts_enabled: false,
    hide_settings_on_radio: true,
    message_retention_days: 30,
    offline_location_retention_days: 30,
      messages_enabled: true,
      status_enabled: true,
    system_sound_volume_percent: 8,
    fall_detection_enabled: false,
    fall_detection_sensitivity: 2,
    fall_detection_tilt_delay_seconds: 10,
    fall_detection_prealarm_seconds: 15,
    fall_detection_alarm_sound_enabled: true,
    fall_detection_alarm_volume_percent: 100,
    rx_audio_gain: 1,
  });

  // Group ("channel") membership checklist for the radio being edited.
  const [editGroupIds, setEditGroupIds] = useState<Set<string>>(new Set());
  const [initialGroupIds, setInitialGroupIds] = useState<Set<string>>(new Set());
  const [groupSearch, setGroupSearch] = useState('');

  // Bulk selection + actions across multiple radios at once.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPrimaryGroup, setBulkPrimaryGroup] = useState('');
  const [bulkAddGroup, setBulkAddGroup] = useState('');
  const [bulkRemoveGroup, setBulkRemoveGroup] = useState('');
  const [bulkPriority, setBulkPriority] = useState<Priority>('standard');

  // Single-radio "Operator" cell popover - one modal reused for whichever radio's cell was
  // clicked (see documentation/decisions.md "S380 operator selection capability").
  const [operatorRadio, setOperatorRadio] = useState<Radio | null>(null);
  const [operatorPending, setOperatorPending] = useState<'scan' | 'switch' | 'automatic' | null>(null);
  const [operatorMessage, setOperatorMessage] = useState<string | null>(null);
  const [operatorScanResult, setOperatorScanResult] = useState<MobileNetworkOperator[]>([]);
  const [operatorConfirmTarget, setOperatorConfirmTarget] = useState<MobileNetworkOperator | null>(null);
  const [wifiRadio, setWifiRadio] = useState<Radio | null>(null);
  const [locationRadio, setLocationRadio] = useState<Radio | null>(null);
  const [location, setLocation] = useState<{ latitude: number; longitude: number; accuracy: number | null; source: string; fix_time: string | null } | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  // Bulk "move all selected radios to the same network" - scan on one reference radio, then
  // apply the chosen operator to every selected radio. Each radio still runs its own local,
  // backend-independent watchdog/rollback (see MobileNetworkManager.kt) - this only relays the
  // switch request to each one, never waits for or implements the rollback itself.
  const [bulkNetworkOpen, setBulkNetworkOpen] = useState(false);
  const [bulkNetworkReferenceId, setBulkNetworkReferenceId] = useState('');
  const [bulkNetworkPending, setBulkNetworkPending] = useState<'scan' | 'apply' | 'automatic' | null>(null);
  const [bulkNetworkMessage, setBulkNetworkMessage] = useState<string | null>(null);
  const [bulkNetworkScanResult, setBulkNetworkScanResult] = useState<MobileNetworkOperator[]>([]);
  const [bulkNetworkConfirmTarget, setBulkNetworkConfirmTarget] = useState<MobileNetworkOperator | null>(null);
  const [bulkWifiOpen, setBulkWifiOpen] = useState(false);
  const [bulkWifiReferenceId, setBulkWifiReferenceId] = useState('');
  const [bulkWifiPending, setBulkWifiPending] = useState<'scan' | 'apply' | null>(null);
  const [bulkWifiMessage, setBulkWifiMessage] = useState<string | null>(null);
  const [bulkWifiNetworks, setBulkWifiNetworks] = useState<WifiNetwork[]>([]);
  const [bulkWifiSelectedNetwork, setBulkWifiSelectedNetwork] = useState<WifiNetwork | null>(null);
  const [bulkWifiPassword, setBulkWifiPassword] = useState('');

  const openLocationModal = async (radio: Radio) => {
    setLocationRadio(radio);
    setLocation(null);
    setLocationError(null);
    setLocationLoading(true);
    try {
      const info = await getDeviceInfo(radio.id);
      if (info.latitude == null || info.longitude == null) {
        setLocation(null);
      } else {
        setLocation({
          latitude: info.latitude,
          longitude: info.longitude,
          accuracy: info.accuracy_m,
          source: info.location_provider || 'Unknown',
          fix_time: info.location_timestamp ?? null,
        });
      }
    } catch (err) {
      setLocationError(err instanceof Error ? err.message : 'Failed to load last known location');
    } finally {
      setLocationLoading(false);
    }
  };

  const closeLocationModal = () => {
    setLocationRadio(null);
    setLocation(null);
    setLocationError(null);
  };

  // Skip auto-refresh while the edit modal is open so it can't clobber in-progress edits.
  const showFormRef = useRef(showForm);
  useEffect(() => {
    showFormRef.current = showForm;
  }, [showForm]);

  useEffect(() => {
    loadRadios();
    const interval = setInterval(() => {
      if (!showFormRef.current) loadRadios(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [orgId]);

  const loadRadios = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [data, all, groupsData, statuses, sosEvents] = await Promise.all([
        getRadiosByOrg(orgId),
        // Platform-only (Android IDs are inventory data): other users simply don't see them.
        getAllRadios().catch(() => []),
        getGroupsByOrg(orgId),
        getRadioAvailabilityStatuses(orgId).catch(() => [] as RadioAvailabilityStatus[]),
        getUnresolvedSosEvents(orgId).catch(() => [] as SosEvent[]),
      ]);
      setRadios(data);
      setGroups(groupsData);
      setAvailabilityStatuses(statuses);
      setActiveAlarms(sosEvents);
      // GET /api/radios doesn't carry android_id; merge it in from the admin-wide listing.
      const map: Record<string, string | null> = {};
      all.forEach((r) => {
        map[r.id] = r.android_id;
      });
      setAndroidIds(map);
    } catch (err) {
      console.error('Failed to load radios:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Man Down / SOS active-alarm lookup - correlated by human_radio_id (e.g. "K0001"), the only
  // stable identifier SosEvent shares with a Radio row (see documentation/protocol.md "Fall
  // detection / Man Down").
  const activeAlarmForRadio = (radio: Radio): SosEvent | undefined =>
    activeAlarms.find((e) => e.human_radio_id === radio.radio_id);

  const alarmLabel = (alarm: SosEvent): string =>
    alarm.sos_type === 'fall_detected' ? 'Man Down alarm' : 'SOS alarm';

  const handleResolveAlarm = async (event: SosEvent) => {
    setResolvingAlarmId(event.id);
    try {
      await resolveSosEvent(event.org_id, event.id);
      setActiveAlarms((prev) => prev.filter((e) => e.id !== event.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve alarm');
    } finally {
      setResolvingAlarmId(null);
    }
  };

  const openCallAlertModal = (radio: Radio) => {
    setCallAlertRadio(radio);
    setCallAlertMessage('');
    setError(null);
  };

  const closeCallAlertModal = () => {
    if (callAlertId === null) {
      setCallAlertRadio(null);
      setCallAlertMessage('');
    }
  };

  const handleCallAlert = async () => {
    if (!callAlertRadio) return;
    setCallAlertId(callAlertRadio.id);
    setError(null);
    try {
      await sendCallAlert(callAlertRadio.id, orgId, callAlertMessage.trim());
      setCallAlertRadio(null);
      setCallAlertMessage('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send Call Alert');
    } finally {
      setCallAlertId(null);
    }
  };

  const handleSendDirectMessage = async () => {
    if ((!messageRadio && !messageToAll) || !messageText.trim()) return;
    setMessageSending(true);
    setError(null);
    try {
      if (messageToAll) {
        const result = await sendDirectMessageToAll(orgId, messageText.trim());
        if (result.failed > 0) throw new Error(`Message sent to ${result.sent} radios; ${result.failed} failed`);
      } else if (messageRadio) {
        await sendDirectMessage(messageRadio.id, messageText.trim());
      }
      setMessageRadio(null);
      setMessageToAll(false);
      setMessageText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setMessageSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const soundVolume = formData.system_sound_volume_percent ?? 8;
    if (soundVolume < 0 || soundVolume > 100 || !Number.isFinite(soundVolume)) {
      setError('System sound volume must be between 0 and 100.');
      return;
    }
    const fallSensitivity = formData.fall_detection_sensitivity ?? 2;
    if (fallSensitivity < 1 || fallSensitivity > 3 || !Number.isFinite(fallSensitivity)) {
      setError('Fall detection sensitivity must be between 1 and 3.');
      return;
    }
    const fallTiltDelaySeconds = formData.fall_detection_tilt_delay_seconds ?? 10;
    if (fallTiltDelaySeconds < 1 || fallTiltDelaySeconds > 60 || !Number.isFinite(fallTiltDelaySeconds)) {
      setError('Man Down delay must be between 1 and 60 seconds.');
      return;
    }
    const fallPrealarmSeconds = formData.fall_detection_prealarm_seconds ?? 15;
    if (fallPrealarmSeconds < 5 || fallPrealarmSeconds > 60 || !Number.isFinite(fallPrealarmSeconds)) {
      setError('Fall detection pre-alarm duration must be between 5 and 60 seconds.');
      return;
    }
    const fallAlarmVolume = formData.fall_detection_alarm_volume_percent ?? 100;
    if (fallAlarmVolume < 0 || fallAlarmVolume > 100 || !Number.isFinite(fallAlarmVolume)) {
      setError('Man Down prealarm volume must be between 0 and 100.');
      return;
    }
    const rxAudioGain = formData.rx_audio_gain ?? 1;
    if (rxAudioGain < 1 || rxAudioGain > 4 || !Number.isFinite(rxAudioGain)) {
      setError('Received audio boost must be between x1 and x4.');
      return;
    }
    const lowBatteryThreshold = formData.low_battery_threshold_percent ?? 10;
    if (lowBatteryThreshold < 1 || lowBatteryThreshold > 100 || !Number.isFinite(lowBatteryThreshold)) {
      setError('Low battery threshold must be between 1 and 100%.');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        // PUT /api/radios/{id} only updates status; use the admin-wide endpoint for details.
        // Settings of features the radio's model does not support are not sent (the server would
        // refuse enabling them) and keep their stored value.
        await updateRadioDetails(editingId, omitUnsupportedFields({
          radio_id: (formData.radio_id || '').trim(),
          model: (formData.model || '').trim(),
          name: (formData.name || '').trim(),
          primary_group_id: formData.primary_group_id || null,
          startup_group_id: formData.startup_group_id || null,
          priority: formData.priority || 'standard',
          tts_enabled: formData.tts_enabled ?? true,
          tts_language: formData.tts_language || 'da',
          ptt_tone_enabled: formData.ptt_tone_enabled ?? true,
          busy_tone_enabled: formData.busy_tone_enabled ?? true,
          connection_tts_enabled: formData.connection_tts_enabled ?? false,
          incoming_ptt_start_enabled: formData.incoming_ptt_start_enabled ?? true,
          incoming_ptt_stop_enabled: formData.incoming_ptt_stop_enabled ?? true,
          low_battery_threshold_percent: formData.low_battery_threshold_percent ?? 10,
          low_battery_tts_enabled: formData.low_battery_tts_enabled ?? false,
          hide_settings_on_radio: formData.hide_settings_on_radio ?? true,
          system_sound_volume_percent: formData.system_sound_volume_percent ?? 8,
          fall_detection_enabled: formData.fall_detection_enabled ?? false,
          fall_detection_sensitivity: formData.fall_detection_sensitivity ?? 2,
          fall_detection_tilt_delay_seconds: formData.fall_detection_tilt_delay_seconds ?? 10,
          fall_detection_prealarm_seconds: formData.fall_detection_prealarm_seconds ?? 15,
          fall_detection_alarm_sound_enabled: formData.fall_detection_alarm_sound_enabled ?? true,
          fall_detection_alarm_volume_percent: formData.fall_detection_alarm_volume_percent ?? 100,
          direct_call_enabled: formData.direct_call_enabled ?? true,
          secondary_group_id: formData.secondary_group_id || null,
          secondary_group_enabled: formData.secondary_group_enabled ?? false,
          auto_mute_secondary_when_primary_receiving: formData.auto_mute_secondary_when_primary_receiving ?? false,
          rx_audio_gain: formData.rx_audio_gain ?? 1,
        }, formData));
        await updateRadioSOSSettings(editingId, omitUnsupportedFields({
          sos_enabled: formData.sos_enabled ?? true,
          sos_alarm_sound_enabled: formData.sos_alarm_sound_enabled ?? true,
          sos_visual_enabled: formData.sos_visual_enabled ?? true,
          sos_recipient_mode: formData.sos_recipient_mode ?? 'radio',
        }, formData));
        if (displayTimeoutLoaded && displayTimeoutMs !== initialDisplayTimeoutMs) {
          await setDisplayTimeout(editingId, displayTimeoutMs);
        }
        await setMessageRetentionDays(editingId, formData.message_retention_days ?? 30);
        await setOfflineLocationRetentionDays(editingId, formData.offline_location_retention_days ?? 30);
        if (supportsFeature(formData, FEATURE.messages)) await setMessagesEnabled(editingId, formData.messages_enabled ?? true);
        if (supportsFeature(formData, FEATURE.status)) await setStatusEnabled(editingId, formData.status_enabled ?? true);
        // Sent only when changed: every DMR PUT is an explicit decision that bumps the radio's DMR
        // revision and so revokes a local DMR enable on the radio (DmrBox has an explicit revoke).
        const savedDmr = radios.find((r) => r.id === editingId)?.dmr_enabled ?? false;
        if (formData.dmr_supported && (formData.dmr_enabled ?? false) !== savedDmr) {
          await setDmrEnabled(editingId, formData.dmr_enabled ?? false);
        }
        // Sent only when changed: turning PoC off immediately stops the radio's PoC audio.
        const savedPoc = radios.find((r) => r.id === editingId)?.poc_enabled ?? true;
        if ((formData.poc_enabled ?? true) !== savedPoc) await setPocEnabled(editingId, formData.poc_enabled ?? true);
        if (buttonAssignments && choicesChanged(buttonAssignments, buttonChoices)) {
          await setRadioButtonAssignments(editingId, assignmentPayload(buttonAssignments, buttonChoices));
        }

        const toAdd = [...editGroupIds].filter((id) => !initialGroupIds.has(id));
        const toRemove = [...initialGroupIds].filter((id) => !editGroupIds.has(id));
        await Promise.all(toAdd.map((groupId) => addMember(orgId, groupId, editingId)));
        await Promise.all(toRemove.map((groupId) => removeMember(orgId, groupId, editingId)));
      } else {
        await createRadio(orgId, formData as Omit<Radio, 'id' | 'created_at' | 'updated_at'>);
      }
      closeForm();
      await loadRadios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save radio');
    } finally {
      setSaving(false);
    }
  };

  // Global per-radio block toggle (Admin > Radios > "Blocked" column) - distinct from the
  // per-group ptt_blocked flag edited inside the Groups manager. A blocked radio immediately
  // loses PTT access on every group (see documentation/protocol.md); the radio itself shows who
  // blocked it and why, so the reason prompt here is purely optional.
  const handleBlockToggle = async (radio: Radio) => {
    if (radio.blocked) {
      if (!confirm(`Unblock ${radio.radio_id}? It will immediately be able to transmit/receive again.`)) return;
      setBlockingId(radio.id);
      setError(null);
      try {
        await unblockRadio(radio.id);
        await loadRadios();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to unblock radio');
      } finally {
        setBlockingId(null);
      }
      return;
    }

    const reason = window.prompt(
      `Block ${radio.radio_id}? It will immediately be unable to transmit/receive on any group.\n\nOptional reason:`
    );
    if (reason === null) return; // cancelled
    setBlockingId(radio.id);
    setError(null);
    try {
      await blockRadio(radio.id, reason.trim() || undefined);
      await loadRadios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to block radio');
    } finally {
      setBlockingId(null);
    }
  };

  // Last reported DMR state of the radio being edited (IRC380), for the DMR box.
  const [editDmrStatus, setEditDmrStatus] = useState<DmrStatus | null>(null);
  const [revokingLocalDmr, setRevokingLocalDmr] = useState(false);

  // An explicit dmr_enabled = false at once (also when it already is false): the new revision
  // revokes the operator's local DMR enable on the radio. The box shows the radio's next report.
  const revokeLocalDmr = async (radioId: string) => {
    setRevokingLocalDmr(true);
    try {
      await setDmrEnabled(radioId, false);
      setFormData((f) => ({ ...f, dmr_enabled: false }));
      setRadios((list) => list.map((r) => (r.id === radioId ? { ...r, dmr_enabled: false } : r)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to turn off DMR');
    } finally {
      setRevokingLocalDmr(false);
    }
  };
  // Button assignments of the radio being edited (null while loading / not loaded).
  const [buttonAssignments, setButtonAssignments] = useState<RadioButtonAssignments | null>(null);
  const [buttonChoices, setButtonChoices] = useState<AssignmentChoices>({});

  const handleEdit = async (radio: Radio) => {
    setEditingId(radio.id);
    setFormData(radio);
    setDisplayTimeoutMs(60000);
    setInitialDisplayTimeoutMs(60000);
    setDisplayTimeoutLoaded(false);
    setButtonAssignments(null);
    setButtonChoices({});
    setEditDmrStatus(null);
    setShowForm(true);
    setGroupSearch('');
    setError(null);
    getRadioButtonAssignments(radio.id)
      .then((data) => {
        setButtonAssignments(data);
        setButtonChoices(initialChoices(data));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load button assignments'));
    try {
      const memberLists = await Promise.all(groups.map((g) => getGroupMembers(orgId, g.id)));
      const memberGroupIds = new Set(
        groups.filter((g, i) => memberLists[i].some((m) => m.radio_id === radio.id)).map((g) => g.id)
      );
      setEditGroupIds(memberGroupIds);
      setInitialGroupIds(memberGroupIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load group memberships');
    }
    try {
      const deviceInfo = await getDeviceInfo(radio.id);
      setEditDmrStatus(deviceInfo.dmr ?? null);
      setDisplayTimeoutLoaded(true);
      if (deviceInfo.display_timeout_ms && DISPLAY_TIMEOUT_OPTIONS.some((option) => option.ms === deviceInfo.display_timeout_ms)) {
        setDisplayTimeoutMs(deviceInfo.display_timeout_ms);
        setInitialDisplayTimeoutMs(deviceInfo.display_timeout_ms);
      }
    } catch {}
  };

  const toggleEditGroup = (groupId: string) => {
    setEditGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = (ids: string[]) => {
    setSelectedIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(ids);
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkSetPrimaryGroup = async () => {
    setBulkBusy(true);
    setError(null);
    try {
      const groupId = bulkPrimaryGroup || null;
      await Promise.all(
        [...selectedIds].map((id) => {
          const radio = radios.find((r) => r.id === id);
          if (!radio) return Promise.resolve();
          return updateRadioDetails(id, {
            radio_id: radio.radio_id,
            model: radio.model,
            name: radio.name,
            primary_group_id: groupId,
            priority: radio.priority,
            tts_enabled: radio.tts_enabled,
            tts_language: radio.tts_language,
            hide_settings_on_radio: radio.hide_settings_on_radio,
            system_sound_volume_percent: radio.system_sound_volume_percent,
          });
        })
      );
      clearSelection();
      setBulkPrimaryGroup('');
      await loadRadios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update primary group for selected radios');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkAddToGroup = async () => {
    if (!bulkAddGroup) return;
    setBulkBusy(true);
    setError(null);
    try {
      await Promise.all([...selectedIds].map((id) => addMember(orgId, bulkAddGroup, id)));
      clearSelection();
      setBulkAddGroup('');
      await loadRadios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add selected radios to group');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkRemoveFromGroup = async () => {
    if (!bulkRemoveGroup) return;
    setBulkBusy(true);
    setError(null);
    try {
      await Promise.all([...selectedIds].map((id) => removeMember(orgId, bulkRemoveGroup, id)));
      clearSelection();
      setBulkRemoveGroup('');
      await loadRadios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove selected radios from group');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkSetPriority = async () => {
    setBulkBusy(true);
    setError(null);
    try {
      await Promise.all(
        [...selectedIds].map((id) => {
          const radio = radios.find((r) => r.id === id);
          if (!radio) return Promise.resolve();
          return updateRadioDetails(id, {
            radio_id: radio.radio_id,
            model: radio.model,
            name: radio.name,
            primary_group_id: radio.primary_group_id,
            priority: bulkPriority,
            tts_enabled: radio.tts_enabled,
            tts_language: radio.tts_language,
            hide_settings_on_radio: radio.hide_settings_on_radio,
            system_sound_volume_percent: radio.system_sound_volume_percent,
          });
        })
      );
      clearSelection();
      await loadRadios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update priority for selected radios');
    } finally {
      setBulkBusy(false);
    }
  };

  // ============ Single-radio Operator cell ============

  const openOperatorModal = (radio: Radio) => {
    setOperatorRadio(radio);
    setOperatorPending(null);
    setOperatorMessage(null);
    setOperatorScanResult([]);
    setOperatorConfirmTarget(null);
    getDeviceInfo(radio.id)
      .then((info) => {
        if (info.mobile_network_scan_result) {
          try {
            setOperatorScanResult(JSON.parse(info.mobile_network_scan_result));
          } catch {
            // ignore malformed/stale scan JSON - the modal still shows the current operator
          }
        }
      })
      .catch(() => undefined);
  };

  const closeOperatorModal = () => {
    setOperatorRadio(null);
    setOperatorConfirmTarget(null);
  };

  // The single-radio Wi-Fi modal is the shared WifiControls (also on Device Details); it holds its
  // own state, request correlation and confirmations.
  const openWifiModal = (radio: Radio) => setWifiRadio(radio);

  // Refreshes the one radio in the table (Operator column) plus the modal's own header, without
  // waiting for the next 10s background poll - mirrors DeviceDetails.tsx's pollDisplayTimeout.
  const refreshOperatorRadio = async (radioId: string) => {
    try {
      const info = await getDeviceInfo(radioId);
      setRadios((prev) =>
        prev.map((r) =>
          r.id === radioId
            ? {
                ...r,
                network_operator: info.network_operator,
                network_operator_numeric: info.network_operator_numeric,
                network_selection_mode: info.network_selection_mode,
              }
            : r
        )
      );
      setOperatorRadio((prev) =>
        prev && prev.id === radioId
          ? {
              ...prev,
              network_operator: info.network_operator,
              network_operator_numeric: info.network_operator_numeric,
              network_selection_mode: info.network_selection_mode,
            }
          : prev
      );
      return info;
    } catch {
      return null;
    }
  };

  const handleOperatorScan = async () => {
    if (!operatorRadio) return;
    setOperatorPending('scan');
    setOperatorMessage('Scanning mobile networks... this can take up to about one minute.');
    const requestedAt = Date.now();
    try {
      await requestMobileNetworkScan(operatorRadio.id);
      const deadline = Date.now() + 100_000;
      const tick = async () => {
        const info = await refreshOperatorRadio(operatorRadio.id);
        const isFresh = info?.mobile_network_scan_updated_at
          ? new Date(info.mobile_network_scan_updated_at).getTime() >= requestedAt
          : false;
        if (isFresh && (info?.mobile_network_scan_status === 'COMPLETED' || info?.mobile_network_scan_status === 'FAILED')) {
          if (info.mobile_network_scan_result) {
            try {
              setOperatorScanResult(JSON.parse(info.mobile_network_scan_result));
            } catch {
              /* ignore */
            }
          }
          setOperatorPending(null);
          setOperatorMessage(info.mobile_network_scan_status === 'COMPLETED' ? 'Scan complete.' : 'Scan failed.');
          return;
        }
        if (Date.now() > deadline) {
          setOperatorPending(null);
          setOperatorMessage('Timed out waiting for the radio to respond.');
          return;
        }
        setTimeout(tick, 5000);
      };
      setTimeout(tick, 3000);
    } catch (e) {
      setOperatorMessage(e instanceof Error ? e.message : 'Failed to request a network scan');
      setOperatorPending(null);
    }
  };

  const handleOperatorSwitchConfirmed = async (target: MobileNetworkOperator) => {
    if (!operatorRadio || !target.mcc_mnc) return;
    const radioId = operatorRadio.id;
    setOperatorConfirmTarget(null);
    setOperatorPending('switch');
    setOperatorMessage(`Switching to ${target.name || target.mcc_mnc}...`);
    const requestedAt = Date.now();
    try {
      await requestMobileNetworkSwitch(radioId, target.mcc_mnc, target.name);
      const deadline = Date.now() + 100_000;
      const tick = async () => {
        const info = await refreshOperatorRadio(radioId);
        const isFresh = info?.mobile_network_switch_updated_at
          ? new Date(info.mobile_network_switch_updated_at).getTime() >= requestedAt
          : false;
        const state = info?.mobile_network_switch_state;
        if (isFresh && (state === 'CONNECTED' || state === 'FAILED' || state === 'IDLE')) {
          setOperatorPending(null);
          setOperatorMessage(info?.mobile_network_switch_message || null);
          return;
        }
        if (Date.now() > deadline) {
          setOperatorPending(null);
          setOperatorMessage('Timed out waiting for the radio to respond.');
          return;
        }
        setTimeout(tick, 5000);
      };
      setTimeout(tick, 3000);
    } catch (e) {
      setOperatorMessage(e instanceof Error ? e.message : 'Failed to request an operator switch');
      setOperatorPending(null);
    }
  };

  const handleOperatorAutomatic = async () => {
    if (!operatorRadio) return;
    const radioId = operatorRadio.id;
    setOperatorPending('automatic');
    setOperatorMessage('Returning to Automatic network selection...');
    const requestedAt = Date.now();
    try {
      await requestMobileNetworkAutomatic(radioId);
      const deadline = Date.now() + 100_000;
      const tick = async () => {
        const info = await refreshOperatorRadio(radioId);
        const isFresh = info?.mobile_network_switch_updated_at
          ? new Date(info.mobile_network_switch_updated_at).getTime() >= requestedAt
          : false;
        const state = info?.mobile_network_switch_state;
        if (isFresh && (state === 'IDLE' || state === 'FAILED')) {
          setOperatorPending(null);
          setOperatorMessage(info?.mobile_network_switch_message || null);
          return;
        }
        if (Date.now() > deadline) {
          setOperatorPending(null);
          setOperatorMessage('Timed out waiting for the radio to respond.');
          return;
        }
        setTimeout(tick, 5000);
      };
      setTimeout(tick, 3000);
    } catch (e) {
      setOperatorMessage(e instanceof Error ? e.message : 'Failed to request automatic selection');
      setOperatorPending(null);
    }
  };

  // ============ Bulk "move to network" ============

  const openBulkNetworkModal = () => {
    setBulkNetworkReferenceId([...selectedIds][0] || '');
    setBulkNetworkPending(null);
    setBulkNetworkMessage(null);
    setBulkNetworkScanResult([]);
    setBulkNetworkConfirmTarget(null);
    setBulkNetworkOpen(true);
  };

  const closeBulkNetworkModal = () => {
    setBulkNetworkOpen(false);
    setBulkNetworkConfirmTarget(null);
  };

  const handleBulkNetworkScan = async () => {
    if (!bulkNetworkReferenceId) return;
    setBulkNetworkPending('scan');
    setBulkNetworkScanResult([]);
    setBulkNetworkMessage('Scanning mobile networks on the reference radio... this can take up to about one minute.');
    const requestedAt = Date.now();
    try {
      await requestMobileNetworkScan(bulkNetworkReferenceId);
      const deadline = Date.now() + 100_000;
      const tick = async () => {
        let info;
        try {
          info = await getDeviceInfo(bulkNetworkReferenceId);
        } catch {
          if (Date.now() > deadline) {
            setBulkNetworkPending(null);
            setBulkNetworkMessage('Timed out waiting for the radio to respond.');
          } else {
            setTimeout(tick, 5000);
          }
          return;
        }
        const isFresh = info.mobile_network_scan_updated_at
          ? new Date(info.mobile_network_scan_updated_at).getTime() >= requestedAt
          : false;
        if (isFresh && (info.mobile_network_scan_status === 'COMPLETED' || info.mobile_network_scan_status === 'FAILED')) {
          if (info.mobile_network_scan_result) {
            try {
              setBulkNetworkScanResult(JSON.parse(info.mobile_network_scan_result));
            } catch {
              /* ignore */
            }
          }
          setBulkNetworkPending(null);
          setBulkNetworkMessage(info.mobile_network_scan_status === 'COMPLETED' ? 'Scan complete - pick a network below.' : 'Scan failed.');
          return;
        }
        if (Date.now() > deadline) {
          setBulkNetworkPending(null);
          setBulkNetworkMessage('Timed out waiting for the radio to respond.');
          return;
        }
        setTimeout(tick, 5000);
      };
      setTimeout(tick, 3000);
    } catch (e) {
      setBulkNetworkMessage(e instanceof Error ? e.message : 'Failed to request a network scan');
      setBulkNetworkPending(null);
    }
  };

  // Fire-and-forget to every selected radio - each radio's own local watchdog (never this
  // client) decides success/rollback, so this never waits for N radios to individually confirm
  // before reporting back; it only confirms the requests were accepted.
  const handleBulkNetworkApply = async (target: MobileNetworkOperator) => {
    if (!target.mcc_mnc) return;
    setBulkNetworkConfirmTarget(null);
    setBulkNetworkPending('apply');
    const ids = [...selectedIds];
    setBulkNetworkMessage(`Requesting switch to ${target.name || target.mcc_mnc} on ${ids.length} radio(s)...`);
    const results = await Promise.allSettled(ids.map((id) => requestMobileNetworkSwitch(id, target.mcc_mnc!, target.name)));
    const failed = results.filter((r) => r.status === 'rejected').length;
    setBulkNetworkMessage(
      failed === 0
        ? `Requested on all ${ids.length} radio(s). Each radio will report its own result (Operator column / Device Details).`
        : `Requested on ${ids.length - failed} of ${ids.length} radio(s) - ${failed} failed to receive the command (likely offline).`
    );
    setBulkNetworkPending(null);
    setTimeout(() => loadRadios(true), 5000);
  };

  const handleBulkNetworkAutomatic = async () => {
    setBulkNetworkPending('automatic');
    const ids = [...selectedIds];
    setBulkNetworkMessage(`Requesting Automatic selection on ${ids.length} radio(s)...`);
    const results = await Promise.allSettled(ids.map((id) => requestMobileNetworkAutomatic(id)));
    const failed = results.filter((r) => r.status === 'rejected').length;
    setBulkNetworkMessage(
      failed === 0
        ? `Requested on all ${ids.length} radio(s).`
        : `Requested on ${ids.length - failed} of ${ids.length} radio(s) - ${failed} failed to receive the command (likely offline).`
    );
    setBulkNetworkPending(null);
    setTimeout(() => loadRadios(true), 5000);
  };

  const openBulkWifiModal = () => {
    setBulkWifiReferenceId([...selectedIds][0] || '');
    setBulkWifiPending(null);
    setBulkWifiMessage(null);
    setBulkWifiNetworks([]);
    setBulkWifiSelectedNetwork(null);
    setBulkWifiPassword('');
    setBulkWifiOpen(true);
  };

  const closeBulkWifiModal = () => {
    setBulkWifiOpen(false);
    setBulkWifiSelectedNetwork(null);
    setBulkWifiPassword('');
  };

  const handleBulkWifiScan = async () => {
    if (!bulkWifiReferenceId) return;
    setBulkWifiPending('scan');
    setBulkWifiNetworks([]);
    setBulkWifiSelectedNetwork(null);
    setBulkWifiPassword('');
    setBulkWifiMessage('Scanning Wi‑Fi networks on the reference radio...');

    try {
      const ticket = await requestWifiScan(bulkWifiReferenceId);
      const deadline = Date.now() + 70_000;
      const tick = async () => {
        try {
          const info = await getDeviceInfo(bulkWifiReferenceId);
          // Only the radio's answer to THIS scan request, never an older result.
          const state = wifiResultState('scan', info, ticket);
          if (state === 'superseded') {
            setBulkWifiPending(null);
            setBulkWifiMessage('Another Wi‑Fi scan of the reference radio replaced this one.');
            return;
          }

          if (state === 'done') {
            if (info.wifi_scan_result) {
              try {
                const parsed = JSON.parse(info.wifi_scan_result) as WifiNetwork[];
                setBulkWifiNetworks(parsed);
              } catch {
                /* ignore malformed/stale scan JSON */
              }
            }
            setBulkWifiPending(null);
            setBulkWifiMessage(info.wifi_scan_status === 'COMPLETED' ? 'Scan complete - pick a network below.' : 'Scan failed.');
            return;
          }

          if (Date.now() > deadline) {
            setBulkWifiPending(null);
            setBulkWifiMessage('Timed out waiting for the reference radio to respond.');
            return;
          }

          setTimeout(tick, 3000);
        } catch {
          if (Date.now() > deadline) {
            setBulkWifiPending(null);
            setBulkWifiMessage('Timed out waiting for the reference radio to respond.');
            return;
          }
          setTimeout(tick, 3000);
        }
      };

      setTimeout(tick, 2000);
    } catch (e) {
      setBulkWifiMessage(e instanceof Error ? e.message : 'Failed to request a Wi‑Fi scan');
      setBulkWifiPending(null);
    }
  };

  const handleBulkWifiApply = async () => {
    if (!bulkWifiSelectedNetwork) return;
    setBulkWifiPending('apply');
    const ids = [...selectedIds];
    setBulkWifiMessage(`Sending Wi‑Fi profile for ${bulkWifiSelectedNetwork.ssid} to ${ids.length} radio(s)...`);
    const secret = bulkWifiPassword;
    // Cleared as soon as the requests are sent, whatever their outcome.
    setBulkWifiPassword('');
    const results = await Promise.allSettled(ids.map((id) => requestWifiConnect(id, bulkWifiSelectedNetwork.ssid, secret)));
    const failed = results.filter((r) => r.status === 'rejected').length;
    setBulkWifiMessage(
      failed === 0
        ? `Sent Wi‑Fi profile for ${bulkWifiSelectedNetwork.ssid} to all ${ids.length} radio(s). They will keep it in memory and connect automatically when in range.`
        : `Sent Wi‑Fi profile to ${ids.length - failed} of ${ids.length} radio(s) - ${failed} failed (likely offline).`
    );
    setBulkWifiPending(null);
    setTimeout(() => loadRadios(true), 5000);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ radio_id: '', model: 'Inrico S380', name: '', priority: 'standard', tts_enabled: true, tts_language: 'da', ptt_tone_enabled: true, busy_tone_enabled: true, connection_tts_enabled: false, incoming_ptt_start_enabled: true, incoming_ptt_stop_enabled: true, sos_enabled: true, sos_alarm_sound_enabled: true, sos_visual_enabled: true, sos_recipient_mode: 'radio', low_battery_threshold_percent: 10, low_battery_tts_enabled: false, hide_settings_on_radio: true, system_sound_volume_percent: 8, fall_detection_enabled: false, fall_detection_sensitivity: 2, fall_detection_tilt_delay_seconds: 10, fall_detection_prealarm_seconds: 15, fall_detection_alarm_sound_enabled: true, fall_detection_alarm_volume_percent: 100, direct_call_enabled: true, secondary_group_enabled: false, auto_mute_secondary_when_primary_receiving: false });
    setDisplayTimeoutMs(60000);
    setInitialDisplayTimeoutMs(60000);
    setDisplayTimeoutLoaded(false);
    setEditGroupIds(new Set());
    setInitialGroupIds(new Set());
    setButtonAssignments(null);
    setButtonChoices({});
    setGroupSearch('');
    setError(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-500/20 text-green-400 border-green-500';
      case 'offline':
        return 'bg-gray-500/20 text-gray-400 border-gray-500';
      case 'transmitting':
        return 'bg-red-500/20 text-red-400 border-red-500';
      case 'low_battery':
        return 'bg-red-500/20 text-red-400 border-red-500';
      default:
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500';
    }
  };

  const getBatteryColor = (level: number, threshold: number) => {
    if (level <= threshold) return 'bg-red-500/20 text-red-400 border-red-500';
    if (level <= Math.min(100, threshold + 10)) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500';
    return 'bg-green-500/20 text-green-400 border-green-500';
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc((prev) => !prev);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const sortIndicator = (field: SortField) => (sortField === field ? (sortAsc ? ' ▲' : ' ▼') : '');

  const filteredRadios = radios.filter((r) => {
    if (onlineOnly && r.status !== 'online') return false;
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      (r.radio_id || '').toLowerCase().includes(q) ||
      (r.name || '').toLowerCase().includes(q) ||
      (r.model || '').toLowerCase().includes(q) ||
      (r.status || '').toLowerCase().includes(q) ||
      (r.availability_status_name || '').toLowerCase().includes(q) ||
      (androidIds[r.id] || '').toLowerCase().includes(q)
    );
  });

  const handleAvailabilityStatusChange = async (radioId: string, statusId: string | null) => {
    const statusName = statusId ? availabilityStatuses.find((s) => s.id === statusId)?.name ?? null : null;
    try {
      await setRadioAvailabilityStatus(orgId, radioId, statusId);
      setRadios((current) => current.map((radio) =>
        radio.id === radioId ? { ...radio, availability_status_id: statusId, availability_status_name: statusName } : radio
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update radio status');
    }
  };

  const sortedRadios = [...filteredRadios].sort((a, b) => {
    if (sortField === 'priority') {
      const va = priorityOrder[a.priority] ?? 1;
      const vb = priorityOrder[b.priority] ?? 1;
      if (va !== vb) return sortAsc ? va - vb : vb - va;
      return 0;
    }
    const value = (radio: Radio) => radio[sortField];
    const va = (value(a) ?? '').toString().toLowerCase();
    const vb = (value(b) ?? '').toString().toLowerCase();
    if (va < vb) return sortAsc ? -1 : 1;
    if (va > vb) return sortAsc ? 1 : -1;
    return 0;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">📡 Radios</h2>
      </div>

      {error && (
        <div className="bg-red-900/40 border border-red-700 rounded-lg p-3 text-red-200">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search devices..."
          className="w-full max-w-[25ch] bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
        />
        <button
          type="button"
          onClick={() => setOnlineOnly((current) => !current)}
          aria-pressed={onlineOnly}
          className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
            onlineOnly
              ? 'bg-green-500/20 text-green-300 border-green-500'
              : 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700'
          }`}
        >
          {onlineOnly ? '✓ Online only' : 'Show online only'}
        </button>
        <button
          type="button"
          onClick={() => { setMessageRadio(null); setMessageToAll(true); setMessageText(''); }}
          className="px-3 py-2 rounded-lg text-sm font-semibold border border-blue-500 bg-blue-500/20 text-blue-200 hover:bg-blue-500/30 transition-colors"
        >
          Send message to all
        </button>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-gray-800 border border-blue-700 rounded-lg p-4 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-blue-300 whitespace-nowrap">
            {selectedIds.size} radio{selectedIds.size === 1 ? '' : 's'} selected
          </span>

          <div className="flex items-center gap-2">
            <select
              value={bulkPrimaryGroup}
              onChange={(e) => setBulkPrimaryGroup(e.target.value)}
              disabled={bulkBusy}
              className="bg-gray-900 border border-gray-600 rounded px-2 py-1.5 text-sm text-white disabled:opacity-50"
            >
              <option value="">None</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            <button
              onClick={handleBulkSetPrimaryGroup}
              disabled={bulkBusy}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded whitespace-nowrap"
            >
              Set Primary Group
            </button>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={bulkPriority}
              onChange={(e) => setBulkPriority(e.target.value as Priority)}
              disabled={bulkBusy}
              className="bg-gray-900 border border-gray-600 rounded px-2 py-1.5 text-sm text-white disabled:opacity-50"
            >
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <button
              onClick={handleBulkSetPriority}
              disabled={bulkBusy}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded whitespace-nowrap"
            >
              Set Priority
            </button>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={bulkAddGroup}
              onChange={(e) => setBulkAddGroup(e.target.value)}
              disabled={bulkBusy}
              className="bg-gray-900 border border-gray-600 rounded px-2 py-1.5 text-sm text-white disabled:opacity-50"
            >
              <option value="">Select group...</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            <button
              onClick={handleBulkAddToGroup}
              disabled={bulkBusy || !bulkAddGroup}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded whitespace-nowrap"
            >
              Add to Group
            </button>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={bulkRemoveGroup}
              onChange={(e) => setBulkRemoveGroup(e.target.value)}
              disabled={bulkBusy}
              className="bg-gray-900 border border-gray-600 rounded px-2 py-1.5 text-sm text-white disabled:opacity-50"
            >
              <option value="">Select group...</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            <button
              onClick={handleBulkRemoveFromGroup}
              disabled={bulkBusy || !bulkRemoveGroup}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded whitespace-nowrap"
            >
              Remove from Group
            </button>
          </div>

          <button
            onClick={openBulkNetworkModal}
            disabled={bulkBusy}
            className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded whitespace-nowrap"
          >
            📡 Move to Network...
          </button>

          <button
            onClick={openBulkWifiModal}
            disabled={bulkBusy}
            className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded whitespace-nowrap"
          >
            📶 Configure Wi‑Fi...
          </button>

          <button
            onClick={clearSelection}
            disabled={bulkBusy}
            className="ml-auto bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-gray-200 text-sm font-medium py-1.5 px-3 rounded"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Radios List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading radios...</p>
        </div>
      ) : radios.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">
          <p className="text-lg">No radios found. Add one to get started.</p>
        </div>
      ) : sortedRadios.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">
          <p className="text-lg">No devices match your search.</p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-visible">
          <table className="w-full text-left">
            <thead className="bg-gray-900 text-gray-400 text-sm">
              <tr>
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={sortedRadios.length > 0 && sortedRadios.every((r) => selectedIds.has(r.id))}
                    onChange={() => toggleSelectAllVisible(sortedRadios.map((r) => r.id))}
                  />
                </th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('radio_id')}>
                  Radio ID{sortIndicator('radio_id')}
                </th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white min-w-[260px]" onClick={() => handleSort('name')}>
                  Radio Name{sortIndicator('name')}
                </th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('model')}>
                  Model{sortIndicator('model')}
                </th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('status')}>
                  Online{sortIndicator('status')}
                </th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('availability_status_name')}>
                  Status{sortIndicator('availability_status_name')}
                </th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('priority')}>
                  Priority{sortIndicator('priority')}
                </th>
                <th className="px-4 py-3 min-w-[220px]">Primary Group</th>
                <th className="px-4 py-3">Battery</th>
                <th className="px-4 py-3">Charging</th>
                <th className="px-4 py-3">Wi-Fi</th>
                <th className="px-4 py-3">Operator</th>
                <th className="px-4 py-3">Message</th>
                <th className="px-4 py-3">Call Alert</th>
                <th className="px-4 py-3">Blocked</th>
                <th className="px-4 py-3">Last Seen</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {sortedRadios.map((radio) => (
                <tr key={radio.id} className={`border-t border-gray-700 ${statusMenuRadioId === radio.id ? 'relative z-30' : ''}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(radio.id)}
                      onChange={() => toggleSelected(radio.id)}
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-300">{radio.radio_id}</td>
                  <td className="px-4 py-3 font-medium min-w-[260px]">
                    {radio.name || '—'}
                    {activeAlarmForRadio(radio) && (() => {
                      const alarm = activeAlarmForRadio(radio);
                      return alarm ? (
                        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border bg-red-500/20 text-red-400 border-red-500 animate-pulse">
                          🚨 {alarmLabel(alarm)}
                        </span>
                      ) : null;
                    })()}
                  </td>
                  <td className="px-4 py-3 text-gray-300">{radio.model || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${getStatusColor(radio.status)}`}>
                      {radio.status}
                    </span>
                  </td>
                  <td className={`px-4 py-3 relative ${statusMenuRadioId === radio.id ? 'z-30' : ''}`}>
                    <button
                      type="button"
                      onClick={() => setStatusMenuRadioId((current) => current === radio.id ? null : radio.id)}
                      disabled={!orgId}
                      aria-haspopup="menu"
                      aria-expanded={statusMenuRadioId === radio.id}
                      className="px-2 py-0.5 rounded-full text-xs font-bold border border-white/30 hover:brightness-110 disabled:opacity-50"
                      style={{ backgroundColor: radio.availability_status_color ?? '#6B7280', color: '#FFFFFF' }}
                    >
                      {radio.availability_status_name === 'None' ? 'Status' : (radio.availability_status_name || 'Status')}
                    </button>
                    {statusMenuRadioId === radio.id && (
                      <div role="menu" className="absolute z-50 left-4 mt-2 min-w-32 overflow-hidden rounded border border-gray-600 bg-gray-900 shadow-lg">
                        {availabilityStatuses.map((status) => (
                          <button
                            key={status.id}
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              handleAvailabilityStatusChange(radio.id, status.id);
                              setStatusMenuRadioId(null);
                            }}
                            className="block w-full px-3 py-2 text-left text-xs text-white hover:bg-gray-700"
                          >
                            {status.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {radio.status === 'offline' ? '-' : (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold border capitalize ${getPriorityColor(radio.priority)}`}>
                        {radio.priority || 'standard'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300 min-w-[220px]">
                    {radio.poc_enabled === false ? (
                      <span
                        className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-amber-900/60 text-amber-300 border border-amber-700"
                        title="PoC is turned off for this radio: no Primary Group, Secondary Group or direct PoC audio. Its groups stay assigned."
                        data-poc-off
                      >
                        PoC OFF
                      </span>
                    ) : radio.status === 'offline' ? '-' : (radio.primary_group_name || '—')}
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {radio.status === 'offline' || radio.battery_level == null ? '-' : (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${getBatteryColor(radio.battery_level, radio.low_battery_threshold_percent ?? 10)}`}>
                        {radio.battery_level}%
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {radio.status === 'offline' ? '-' : radio.charging_state ? (
                      radio.charging_state.toLowerCase() === 'charging' ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold border bg-green-500/20 text-green-400 border-green-500">
                          ⚡
                        </span>
                      ) : (
                        <span title={radio.charging_state}>-</span>
                      )
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => openWifiModal(radio)}
                      disabled={radio.status !== 'online'}
                      title={radio.status === 'online' ? 'Scan or connect Wi-Fi' : 'Wi-Fi control requires an online radio'}
                      className={`px-2 py-0.5 rounded-full text-xs font-bold border disabled:opacity-40 disabled:cursor-not-allowed ${
                        radio.wifi_ssid
                          ? 'bg-green-500/20 text-green-400 border-green-500'
                          : radio.wifi_enabled
                            ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500'
                            : 'bg-gray-500/20 text-gray-300 border-gray-500'
                      }`}
                    >
                      Wi-Fi
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    {radio.status === 'offline' ? '-' : radio.network_operator ? (
                      <button
                        onClick={() => openOperatorModal(radio)}
                        title="Select operator or return to automatic"
                        className={`px-2 py-0.5 rounded-full text-xs font-bold border ${
                          radio.network_selection_mode === 'MANUAL'
                            ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500'
                            : 'bg-green-500/20 text-green-400 border-green-500'
                        }`}
                      >
                        {radio.network_operator}
                      </button>
                    ) : (
                      <button
                        onClick={() => openOperatorModal(radio)}
                        title="Select operator or return to automatic"
                        className="text-gray-500 hover:text-blue-400 text-sm"
                      >
                        —
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => { setMessageRadio(radio); setMessageText(''); setError(null); }}
                      disabled={radio.messages_enabled === false || !supportsFeature(radio, FEATURE.messages)}
                      title={!supportsFeature(radio, FEATURE.messages) ? "This radio's model does not support Messages" : radio.messages_enabled === false ? 'Messages are disabled for this radio' : 'Send message to this radio; offline radios receive it when they reconnect'}
                      className="px-2 py-0.5 rounded-full text-xs font-bold border bg-blue-500/20 text-blue-300 border-blue-500 hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ✉ Message
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => openCallAlertModal(radio)}
                      disabled={radio.status !== 'online' || callAlertId === radio.id}
                      title={radio.status !== 'online' ? 'Radio must be online to receive a Call Alert' : 'Send Call Alert to this radio'}
                      className="px-2 py-0.5 rounded-full text-xs font-bold border bg-orange-500/20 text-orange-400 border-orange-500 hover:bg-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {callAlertId === radio.id ? '...' : '📞 Alert'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleBlockToggle(radio)}
                      disabled={blockingId === radio.id}
                      title={
                        radio.blocked
                          ? `Blocked by ${radio.blocked_by_username || 'unknown'}${radio.blocked_reason ? `: ${radio.blocked_reason}` : ''}`
                          : 'Block this radio from transmitting/receiving on any group'
                      }
                      className={`px-2 py-0.5 rounded-full text-xs font-bold border disabled:opacity-50 ${
                        radio.blocked
                          ? 'bg-red-500/20 text-red-400 border-red-500 hover:bg-red-500/30'
                          : 'bg-gray-700/50 text-gray-400 border-gray-600 hover:bg-gray-700'
                      }`}
                    >
                      {blockingId === radio.id ? '...' : radio.blocked ? 'Blocked' : 'Block'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-sm">
                    {radio.last_seen ? (
                      <button
                        onClick={() => openLocationModal(radio)}
                        className="text-left text-blue-300 hover:text-blue-200 hover:underline"
                        title="Show last known location"
                      >
                        {formatLastSeen(radio.last_seen)}
                      </button>
                    ) : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleEdit(radio)}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-1.5 px-3 rounded"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-gray-400 text-sm">
        {search || onlineOnly
          ? `Showing ${sortedRadios.length} of ${radios.length} radios`
          : `Total radios: ${radios.length}`}
      </p>

      {/* Edit Radio Modal */}
      {showForm && (
        <div data-management-modal className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-5xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <h3 className="text-2xl font-bold text-white">{editingId ? 'Edit Radio' : 'Add New Radio'}</h3>

            {error && (
              <div className="bg-red-900/40 border border-red-700 rounded p-3 text-red-200 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <label className="block">
                    <p className="text-sm font-semibold text-gray-300 mb-1">Radio ID</p>
                    <input
                      type="text"
                      value={formData.radio_id || ''}
                      onChange={(e) => setFormData({ ...formData, radio_id: e.target.value })}
                      disabled={!!editingId}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="e.g., S0001"
                      required
                    />
                  </label>
                  <label className="block">
                    <p className="text-sm font-semibold text-gray-300 mb-1">Name</p>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      placeholder="e.g., Dispatch 01"
                      required
                    />
                  </label>
                  <label className="block">
                    <p className="text-sm font-semibold text-gray-300 mb-1">Model</p>
                    <select
                      value={formData.model || 'Inrico S380'}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      disabled={!!editingId}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option>Inrico S380</option>
                      <option>Inrico T740A</option>
                      <option>Generic Radio</option>
                    </select>
                  </label>

                  {editingId && (formData.poc_enabled ?? true) === false && (
                    <p className="text-xs text-amber-300" data-poc-groups-inactive>
                      Primary Group is disabled (PoC OFF, Radio-Specific Settings &gt; Features): the groups below stay
                      assigned but are inactive until it is enabled again.
                    </p>
                  )}

                  <label className="block">
                    <p className="text-sm font-semibold text-gray-300 mb-1">Primary Group</p>
                    <select
                      value={formData.primary_group_id || ''}
                      onChange={(e) => setFormData({ ...formData, primary_group_id: e.target.value || null })}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    >
                      <option value="">None</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </label>

                  {supportsFeature(formData, FEATURE.secondaryGroup) && <label className="block">
                    <p className="text-sm font-semibold text-gray-300 mb-1">Secondary Group</p>
                    <select
                      value={formData.secondary_group_id || ''}
                      onChange={(e) => setFormData({ ...formData, secondary_group_id: e.target.value || null })}
                      disabled={!(formData.secondary_group_enabled ?? false)}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">None</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </label>}

                  {/* DMR: the IRC380's third radio function, next to the two PoC groups. */}
                  {editingId && formData.dmr_supported && (
                    <DmrBox
                      enabled={formData.dmr_enabled ?? false}
                      status={editDmrStatus}
                      onRevoke={() => revokeLocalDmr(editingId)}
                      revoking={revokingLocalDmr}
                    />
                  )}

                  <label className="block">
                    <p className="text-sm font-semibold text-gray-300 mb-1">Standard Startup Group</p>
                    <select
                      value={formData.startup_group_id || ''}
                      onChange={(e) => setFormData({ ...formData, startup_group_id: e.target.value || null })}
                      className={`w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded focus:ring-2 focus:ring-blue-600 focus:border-transparent ${formData.startup_group_id ? 'text-white' : 'text-green-400'}`}
                    >
                      <option value="" className="text-green-400">Last used Group</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id} className="text-white">{g.name}</option>
                      ))}
                    </select>
                  </label>

                  <label className="block">
                    <p className="text-sm font-semibold text-gray-300 mb-1">Priority</p>
                    <select
                      value={formData.priority || 'standard'}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as Priority })}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    >
                      {PRIORITY_OPTIONS.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </label>

                  {editingId && (
                    <div className="border-t border-gray-700 pt-4 space-y-2">
                      <p className="text-sm font-semibold text-gray-300">Groups</p>
                      <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                          type="checkbox"
                          checked={groups.length > 0 && groups.every((g) => editGroupIds.has(g.id))}
                          onChange={(e) =>
                            setEditGroupIds(e.target.checked ? new Set(groups.map((g) => g.id)) : new Set())
                          }
                        />
                        Select all groups
                      </label>
                      <input
                        type="text"
                        value={groupSearch}
                        onChange={(e) => setGroupSearch(e.target.value)}
                        placeholder="Search..."
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      />
                      <div className="border border-gray-600 rounded max-h-48 overflow-y-auto divide-y divide-gray-700">
                        {groups
                          .filter((g) => g.name.toLowerCase().includes(groupSearch.toLowerCase()))
                          .map((g) => (
                            <label key={g.id} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700/50 cursor-pointer">
                              <input type="checkbox" checked={editGroupIds.has(g.id)} onChange={() => toggleEditGroup(g.id)} />
                              {g.name}
                            </label>
                          ))}
                        {groups.length === 0 && <p className="px-3 py-2 text-sm text-gray-400">No groups in this network.</p>}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  {editingId && (
                    <div className="border border-gray-700 rounded-lg p-4 space-y-3">
                      <p className="text-xl font-bold text-white">Radio-Specific Settings</p>

                      <details className="border-t border-gray-700 pt-3 space-y-3">
                        <summary className="cursor-pointer text-base font-semibold text-white">Features</summary>
                        {hasHiddenFeatureSettings(formData) && (
                          <p className="text-xs text-gray-500" data-unsupported-note>
                            Features this radio's model does not support are not shown (Settings &gt; Model Catalog).
                          </p>
                        )}

                        {/* The all-PoC switch (radios.poc_enabled), every model, default on. */}
                        <PocSwitch
                          enabled={formData.poc_enabled ?? true}
                          saved={radios.find((r) => r.id === editingId)?.poc_enabled ?? true}
                          onChange={(enabled) => setFormData({ ...formData, poc_enabled: enabled })}
                        />

                        {supportsFeature(formData, FEATURE.secondaryGroup) && <>
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={formData.secondary_group_enabled ?? false}
                            onChange={(e) => setFormData({ ...formData, secondary_group_enabled: e.target.checked })}
                          />
                          Enable Secondary Group function
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-300 ml-6">
                          <input
                            type="checkbox"
                            checked={formData.auto_mute_secondary_when_primary_receiving ?? false}
                            onChange={(e) => setFormData({ ...formData, auto_mute_secondary_when_primary_receiving: e.target.checked })}
                            disabled={!(formData.secondary_group_enabled ?? false)}
                          />
                          Auto-mute Secondary Group while Primary Group is receiving
                        </label>
                        <p className="text-xs text-gray-500 -mt-2">Shows a "Secondary Group" box on the radio's screen, below Primary Group, transmitted on via the F3 button (S380). The radio can only ever listen to one group at a time - Primary Group always has priority.</p>
                        </>}

                        {formData.dmr_supported && <>
                        <label className="flex items-center gap-2 text-sm text-gray-300" data-dmr-enable>
                          <input
                            type="checkbox"
                            checked={formData.dmr_enabled ?? false}
                            onChange={(e) => setFormData({ ...formData, dmr_enabled: e.target.checked })}
                          />
                          Enable DMR
                        </label>
                        <p className="text-xs text-gray-500 -mt-2">The radio's built-in DMR module, a radio function of its own next to Primary Group and Secondary Group. By default the Main PTT transmits on the Primary Group and the Secondary PTT (PTT2) on DMR; the "Swap Main PTT: Primary Group / DMR" button action exchanges them. Off: the radio never starts the DMR module.</p>
                        </>}

                        {supportsFeature(formData, FEATURE.fallDetection) && <>
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={formData.fall_detection_enabled ?? false}
                            onChange={(e) => setFormData({ ...formData, fall_detection_enabled: e.target.checked })}
                          />
                          Fall Detection / Man Down (Enable Man Down)
                        </label>
                        <p className="text-xs text-gray-500 -mt-2">Uses the radio's built-in orientation sensor to detect a sustained tilt/fall (Man Down) - the radio being tilted or lying down for longer than the configured delay, not a single impact. When triggered, the radio vibrates/beeps locally with a countdown - pressing the physical PTT button, or the on-radio Cancel button, cancels it. If not cancelled in time, an automatic emergency alert is sent (same alert list as manual SOS).</p>
                        </>}

                        {supportsFeature(formData, FEATURE.sos) && <label className="flex items-center gap-2 text-sm text-gray-300">
                          <input type="checkbox" checked={formData.sos_enabled ?? true} onChange={(e) => setFormData({ ...formData, sos_enabled: e.target.checked })} />
                          Enable SOS function
                        </label>}

                        {supportsFeature(formData, FEATURE.directCall) && <>
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={formData.direct_call_enabled ?? true}
                            onChange={(e) => setFormData({ ...formData, direct_call_enabled: e.target.checked })}
                          />
                          Radio / User function
                        </label>
                        <p className="text-xs text-gray-500 -mt-2">Shows the "Radio / User" box on the radio's screen, letting the operator pick another radio/user and open the Action menu. Disabling this hides the box entirely. The box is also hidden while PoC is disabled for the radio; this setting is kept and applies again when PoC is enabled.</p>
                        </>}

                        {supportsFeature(formData, FEATURE.messages) && <>
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={formData.messages_enabled ?? true}
                            onChange={(e) => setFormData({ ...formData, messages_enabled: e.target.checked })}
                          />
                          Enable Messages
                        </label>
                        <p className="text-xs text-gray-500 -mt-2">Shows the Messages button and local message bank on the radio.</p>
                        </>}

                        {supportsFeature(formData, FEATURE.status) && <>
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={formData.status_enabled ?? true}
                            onChange={(e) => setFormData({ ...formData, status_enabled: e.target.checked })}
                          />
                          Enable Status
                        </label>
                        <p className="text-xs text-gray-500 -mt-2">Shows the Status button on the radio for choosing its availability status.</p>
                        </>}

                        <label className="flex items-center gap-2 text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={formData.hide_settings_on_radio ?? true}
                            onChange={(e) => setFormData({ ...formData, hide_settings_on_radio: e.target.checked })}
                          />
                          Hide Settings on Radio
                        </label>
                        <p className="text-xs text-gray-500 -mt-2">Hides the gear/Settings icon on the radio while it's connected and approved. Always shown while disconnected or awaiting approval, so the radio can never be locked out.</p>
                      </details>

                      {formData.dmr_supported && (
                        <details className="border-t border-gray-700 pt-3 space-y-3" data-dmr-settings-section>
                          <summary className="cursor-pointer text-base font-semibold text-white">DMR settings</summary>
                          <RadioDmrSettings radioId={editingId} orgId={orgId} />
                        </details>
                      )}

                      <details className="border-t border-gray-700 pt-3" data-button-assignments-section>
                        <summary className="cursor-pointer text-base font-semibold text-white">Button assignments</summary>
                        <ButtonAssignmentsEditor data={buttonAssignments} choices={buttonChoices} onChange={setButtonChoices} />
                      </details>

                      <details className="border-t border-gray-700 pt-3 space-y-3">
                        <summary className="cursor-pointer text-base font-semibold text-white">Sound settings</summary>

                      <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                          type="checkbox"
                          checked={formData.tts_enabled ?? true}
                          onChange={(e) => setFormData({ ...formData, tts_enabled: e.target.checked })}
                        />
                        Speak group name aloud (text-to-speech) when the group changes
                      </label>

                      <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                          type="checkbox"
                          checked={formData.ptt_tone_enabled ?? true}
                          onChange={(e) => setFormData({ ...formData, ptt_tone_enabled: e.target.checked })}
                        />
                        Enable PTT tone
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                          type="checkbox"
                          checked={formData.busy_tone_enabled ?? true}
                          onChange={(e) => setFormData({ ...formData, busy_tone_enabled: e.target.checked })}
                        />
                        Enable busy tone
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                          type="checkbox"
                          checked={formData.connection_tts_enabled ?? false}
                          onChange={(e) => setFormData({ ...formData, connection_tts_enabled: e.target.checked })}
                        />
                        Announce connected/disconnected with TTS
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                          type="checkbox"
                          checked={formData.incoming_ptt_start_enabled ?? true}
                          onChange={(e) => setFormData({ ...formData, incoming_ptt_start_enabled: e.target.checked })}
                        />
                        Incoming PTT start notification (1200 Hz)
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                          type="checkbox"
                          checked={formData.incoming_ptt_stop_enabled ?? true}
                          onChange={(e) => setFormData({ ...formData, incoming_ptt_stop_enabled: e.target.checked })}
                        />
                        Incoming PTT stop notification (800 Hz)
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input type="checkbox" checked={formData.sos_alarm_sound_enabled ?? true} onChange={(e) => setFormData({ ...formData, sos_alarm_sound_enabled: e.target.checked })} />
                        Enable SOS alarm sound on radio
                      </label>
                      <label className="block">
                        <p className="text-sm font-semibold text-gray-300 mb-1">TTS Language</p>
                        <select
                          value={formData.tts_language || 'da'}
                          onChange={(e) => setFormData({ ...formData, tts_language: e.target.value as TTSLanguage })}
                          disabled={!(formData.tts_enabled ?? true)}
                          className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {TTS_LANGUAGE_OPTIONS.map((l) => (
                            <option key={l.value} value={l.value}>{l.label}</option>
                          ))}
                        </select>
                      </label>

                      <label className="block">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-semibold text-gray-300">System sound volume</p>
                          <span className="text-sm text-gray-300 tabular-nums">{formData.system_sound_volume_percent ?? 8}%</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={1}
                          value={formData.system_sound_volume_percent ?? 8}
                          onChange={(e) => setFormData({ ...formData, system_sound_volume_percent: Number(e.target.value) })}
                          className="w-full accent-blue-600"
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={formData.system_sound_volume_percent ?? 8}
                            onChange={(e) => setFormData({ ...formData, system_sound_volume_percent: Number(e.target.value) })}
                            className="w-20 px-2 py-1 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          />
                          <span className="text-sm text-gray-400">%</span>
                        </div>
                        {(formData.system_sound_volume_percent ?? 8) < 0 || (formData.system_sound_volume_percent ?? 8) > 100 ? (
                          <p className="text-xs text-red-400 mt-1">Must be between 0 and 100.</p>
                        ) : null}
                      </label>
                      <p className="text-xs text-gray-500 -mt-2">
                        Shared volume for the app's own local system sounds on this radio (PTT "ready to talk" chirp, channel-busy tone, and other local signal/status sounds) - 0% mutes them entirely. Does not affect received voice audio, the microphone, or the radio's global Android volume.
                      </p>

                      <label className="block">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-semibold text-gray-300">Received audio boost</p>
                          <span className="text-sm text-gray-300 tabular-nums">x{(formData.rx_audio_gain ?? 1).toFixed(1)}</span>
                        </div>
                        <input
                          type="range"
                          min={1}
                          max={4}
                          step={0.1}
                          value={formData.rx_audio_gain ?? 1}
                          onChange={(e) => setFormData({ ...formData, rx_audio_gain: Number(e.target.value) })}
                          className="w-full accent-blue-600"
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="number"
                            min={1}
                            max={4}
                            step={0.1}
                            value={formData.rx_audio_gain ?? 1}
                            onChange={(e) => setFormData({ ...formData, rx_audio_gain: Number(e.target.value) })}
                            className="w-20 px-2 py-1 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          />
                          <span className="text-sm text-gray-400">x</span>
                        </div>
                        {(formData.rx_audio_gain ?? 1) < 1 || (formData.rx_audio_gain ?? 1) > 4 ? (
                          <p className="text-xs text-red-400 mt-1">Must be between x1 and x4.</p>
                        ) : null}
                      </label>
                      <p className="text-xs text-gray-500 -mt-2">
                        Boosts this radio's received (RX) voice audio beyond WebRTC's normal unity gain - useful when even the radio's own volume and any connected Bluetooth headset are already at maximum. x1 (default) is normal/unmodified gain. Never affects the microphone, local system sounds, or the radio's global Android volume.
                      </p>
                      </details>

                      {supportsFeature(formData, FEATURE.fallDetection) && <details className="border-t border-gray-700 pt-3 space-y-3">
                        <summary className="cursor-pointer text-base font-semibold text-white">Man down</summary>

                      {editingId && (() => {
                        const editingRadio = radios.find((r) => r.id === editingId);
                        const alarm = editingRadio ? activeAlarmForRadio(editingRadio) : undefined;
                        if (!alarm) return null;
                        return (
                          <div className="rounded border border-red-500 bg-red-500/10 p-3 space-y-2">
                            <p className="text-sm font-bold text-red-400">🚨 Active {alarmLabel(alarm)}</p>
                            <p className="text-xs text-gray-300">Event-ID: <span className="font-mono">{alarm.id}</span></p>
                            <p className="text-xs text-gray-300">Time: {new Date(alarm.created_at).toLocaleString()}</p>
                            <p className="text-xs text-gray-300">Position: {describeAlarmPosition(alarm)}</p>
                            <p className="text-xs text-gray-300">Status: {alarm.acknowledged ? 'Acknowledged' : 'Unacknowledged'}, unresolved</p>
                            {confirmingResolveAlarmId === alarm.id ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-300">Are you sure?</span>
                                <button
                                  type="button"
                                  onClick={() => handleResolveAlarm(alarm)}
                                  disabled={resolvingAlarmId === alarm.id}
                                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold py-1.5 px-3 rounded"
                                >
                                  Yes, resolve
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmingResolveAlarmId(null)}
                                  className="text-xs text-gray-400 underline"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setConfirmingResolveAlarmId(alarm.id)}
                                className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-1.5 px-3 rounded"
                              >
                                Afstil Man Down-alarm
                              </button>
                            )}
                          </div>
                        );
                      })()}

                      <label className="block">
                        <p className="text-sm font-semibold text-gray-300 mb-1">Sensitivity</p>
                        <select
                          value={formData.fall_detection_sensitivity ?? 2}
                          onChange={(e) => setFormData({ ...formData, fall_detection_sensitivity: Number(e.target.value) })}
                          disabled={!(formData.fall_detection_enabled ?? false)}
                          className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value={1}>Low (75° tilt required)</option>
                          <option value={2}>Medium (60° tilt required, default)</option>
                          <option value={3}>High (45° tilt required, triggers more easily)</option>
                        </select>
                      </label>

                      <label className="block">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-semibold text-gray-300">Man Down delay</p>
                          <span className="text-sm text-gray-300 tabular-nums">{formData.fall_detection_tilt_delay_seconds ?? 10}s</span>
                        </div>
                        <input
                          type="range"
                          min={1}
                          max={60}
                          step={1}
                          value={formData.fall_detection_tilt_delay_seconds ?? 10}
                          onChange={(e) => setFormData({ ...formData, fall_detection_tilt_delay_seconds: Number(e.target.value) })}
                          disabled={!(formData.fall_detection_enabled ?? false)}
                          className="w-full accent-blue-600 disabled:opacity-50"
                        />
                        {(formData.fall_detection_tilt_delay_seconds ?? 10) < 1 || (formData.fall_detection_tilt_delay_seconds ?? 10) > 60 ? (
                          <p className="text-xs text-red-400 mt-1">Must be between 1 and 60 seconds.</p>
                        ) : null}
                      </label>
                      <p className="text-xs text-gray-500 -mt-2">
                        How long the radio must stay tilted past the sensitivity's angle before the local pre-alarm starts.
                      </p>

                      <label className="block">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-semibold text-gray-300">Pre-alarm countdown</p>
                          <span className="text-sm text-gray-300 tabular-nums">{formData.fall_detection_prealarm_seconds ?? 15}s</span>
                        </div>
                        <input
                          type="range"
                          min={5}
                          max={60}
                          step={1}
                          value={formData.fall_detection_prealarm_seconds ?? 15}
                          onChange={(e) => setFormData({ ...formData, fall_detection_prealarm_seconds: Number(e.target.value) })}
                          disabled={!(formData.fall_detection_enabled ?? false)}
                          className="w-full accent-blue-600 disabled:opacity-50"
                        />
                        {(formData.fall_detection_prealarm_seconds ?? 15) < 5 || (formData.fall_detection_prealarm_seconds ?? 15) > 60 ? (
                          <p className="text-xs text-red-400 mt-1">Must be between 5 and 60 seconds.</p>
                        ) : null}
                      </label>
                      <p className="text-xs text-gray-500 -mt-2">
                        How long the radio waits, vibrating/beeping, for a physical PTT press or Cancel button before sending the automatic alert.
                      </p>

                      <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                          type="checkbox"
                          checked={formData.fall_detection_alarm_sound_enabled ?? true}
                          onChange={(e) => setFormData({ ...formData, fall_detection_alarm_sound_enabled: e.target.checked })}
                          disabled={!(formData.fall_detection_enabled ?? false)}
                        />
                        Man Down prealarm sound
                      </label>
                      <p className="text-xs text-gray-500 -mt-2">
                        When disabled, the pre-alarm still wakes the screen, vibrates, and counts down - only the audible alarm tone is muted. Never affects the radio's talk/system/media volume - see the volume setting below.
                      </p>

                      <label className="block">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-semibold text-gray-300">Man Down prealarm volume</p>
                          <span className="text-sm text-gray-300 tabular-nums">{formData.fall_detection_alarm_volume_percent ?? 100}%</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={1}
                          value={formData.fall_detection_alarm_volume_percent ?? 100}
                          onChange={(e) => setFormData({ ...formData, fall_detection_alarm_volume_percent: Number(e.target.value) })}
                          disabled={!(formData.fall_detection_enabled ?? false) || !(formData.fall_detection_alarm_sound_enabled ?? true)}
                          className="w-full accent-blue-600 disabled:opacity-50"
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={formData.fall_detection_alarm_volume_percent ?? 100}
                            onChange={(e) => setFormData({ ...formData, fall_detection_alarm_volume_percent: Number(e.target.value) })}
                            disabled={!(formData.fall_detection_enabled ?? false) || !(formData.fall_detection_alarm_sound_enabled ?? true)}
                            className="w-20 px-2 py-1 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50"
                          />
                          <span className="text-sm text-gray-400">%</span>
                        </div>
                        {(formData.fall_detection_alarm_volume_percent ?? 100) < 0 || (formData.fall_detection_alarm_volume_percent ?? 100) > 100 ? (
                          <p className="text-xs text-red-400 mt-1">Must be between 0 and 100.</p>
                        ) : null}
                      </label>
                      <p className="text-xs text-gray-500 -mt-2">
                        This app-controlled gain is the ONLY thing that controls the Man Down alarm's loudness. It never changes the radio's talk volume, system sound volume, or any Android stream volume - the alarm tone's actual maximum loudness is limited by the radio's own current output level, which this setting can never exceed.
                      </p>
                      </details>}

                      <details className="border-t border-gray-700 pt-3">
                        <summary className="cursor-pointer text-base font-semibold text-white">Other</summary>
                        <label className="block pt-3">
                          <p className="text-sm font-semibold text-gray-300 mb-1">Android screen timeout</p>
                          <select
                            value={displayTimeoutMs}
                            onChange={(e) => setDisplayTimeoutMs(Number(e.target.value))}
                            className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          >
                            {DISPLAY_TIMEOUT_OPTIONS.map((option) => (
                              <option key={option.ms} value={option.ms}>{option.label}</option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Controls how long the radio screen stays on without activity. The change is applied when the radio is online.
                          </p>
                        </label>
                        <label className="block pt-3">
                          <p className="text-sm font-semibold text-gray-300 mb-1">Message retention on radio (days)</p>
                          <input
                            type="number"
                            min={1}
                            max={3650}
                            value={formData.message_retention_days ?? 30}
                            onChange={(e) => setFormData({ ...formData, message_retention_days: Number(e.target.value) })}
                            className="w-32 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">The radio automatically removes locally stored messages older than this period.</p>
                        </label>
                        <label className="block pt-3">
                          <p className="text-sm font-semibold text-gray-300 mb-1">Offline GPS retention on radio (days)</p>
                          <input
                            type="number"
                            min={1}
                            max={365}
                            value={formData.offline_location_retention_days ?? 30}
                            onChange={(e) => setFormData({ ...formData, offline_location_retention_days: Number(e.target.value) })}
                            className="w-32 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">Offline GPS fixes are kept in the radio&apos;s local SQLite queue until uploaded or this period expires.</p>
                        </label>
                        <label className="block pt-3">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-semibold text-gray-300">Low battery threshold</p>
                            <span className="text-sm text-gray-300 tabular-nums">{formData.low_battery_threshold_percent ?? 10}%</span>
                          </div>
                          <input
                            type="range"
                            min={1}
                            max={100}
                            step={1}
                            value={formData.low_battery_threshold_percent ?? 10}
                            onChange={(e) => setFormData({ ...formData, low_battery_threshold_percent: Number(e.target.value) })}
                            className="w-full accent-blue-600"
                          />
                          <div className="flex items-center gap-2 mt-2">
                            <input
                              type="number"
                              min={1}
                              max={100}
                              value={formData.low_battery_threshold_percent ?? 10}
                              onChange={(e) => setFormData({ ...formData, low_battery_threshold_percent: Number(e.target.value) })}
                              className="w-20 px-2 py-1 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            />
                            <span className="text-sm text-gray-400">%</span>
                          </div>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-300 pt-2">
                          <input
                            type="checkbox"
                            checked={formData.low_battery_tts_enabled ?? false}
                            onChange={(e) => setFormData({ ...formData, low_battery_tts_enabled: e.target.checked })}
                          />
                          Announce low battery via TTS
                        </label>
                        <p className="text-xs text-gray-500">
                          The warning is announced once when the battery enters the low-battery state. It uses the TTS language selected above: “Batteri lavt. Skift batteri snarest”.
                        </p>
                      </details>

                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Single-radio Operator modal (Admin > Radios > Operator column) */}
      {operatorRadio && (
        <div data-management-modal className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-lg p-6 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-bold text-white">Mobile Network</h3>
                <p className="text-gray-400 text-sm">{operatorRadio.radio_id} · {operatorRadio.name}</p>
              </div>
              <button onClick={closeOperatorModal} className="text-gray-400 hover:text-white">✕</button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-400 uppercase font-semibold">Current operator</p>
                <p className="text-white">{operatorRadio.network_operator || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-semibold">Selection mode</p>
                <p className="text-white">{operatorRadio.network_selection_mode || '—'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleOperatorScan}
                disabled={operatorRadio.status !== 'online' || operatorPending !== null}
                title={operatorRadio.status !== 'online' ? 'Radio must be online' : undefined}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
              >
                {operatorPending === 'scan' ? 'Scanning…' : '📡 Scan networks'}
              </button>
              <button
                onClick={handleOperatorAutomatic}
                disabled={operatorRadio.status !== 'online' || operatorPending !== null}
                title={operatorRadio.status !== 'online' ? 'Radio must be online' : undefined}
                className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
              >
                {operatorPending === 'automatic' ? 'Requesting…' : 'Return to Automatic'}
              </button>
            </div>

            {operatorRadio.status !== 'online' && (
              <p className="text-sm text-amber-400">Radio is offline. Mobile network control is unavailable.</p>
            )}
            {operatorMessage && (
              <div className="flex items-center gap-2">
                {operatorPending && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>}
                <p className="text-sm text-gray-400">{operatorMessage}</p>
              </div>
            )}

            {operatorScanResult.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-gray-400 uppercase font-semibold">Available Networks</p>
                {operatorScanResult.map((op, idx) => {
                  const isCurrent = operatorRadio.network_operator_numeric
                    ? op.mcc_mnc === operatorRadio.network_operator_numeric
                    : op.availability === 'CURRENT';
                  return (
                    <div key={`${op.mcc_mnc}-${idx}`} className="flex items-center justify-between border-t border-gray-700 pt-1">
                      <div>
                        <p className="text-white text-sm">{op.name || 'Unknown'} ({op.mcc_mnc || '?'})</p>
                        <p className="text-gray-400 text-xs">
                          {op.technology} · {isCurrent ? 'Current' : op.availability === 'FORBIDDEN' ? 'Forbidden' : 'Available'}
                        </p>
                      </div>
                      {!isCurrent && op.availability !== 'FORBIDDEN' && (
                        <button
                          onClick={() => setOperatorConfirmTarget(op)}
                          disabled={operatorRadio.status !== 'online' || operatorPending !== null}
                          className="text-blue-400 hover:text-blue-300 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Select
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {operatorConfirmTarget && (
              <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 space-y-2">
                <p className="text-white font-semibold">
                  Switch {operatorRadio.radio_id} to {operatorConfirmTarget.name || operatorConfirmTarget.mcc_mnc}?
                </p>
                <p className="text-sm text-gray-400">
                  If registration or backend connectivity fails, the radio automatically returns to Automatic mode.
                </p>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setOperatorConfirmTarget(null)}
                    className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleOperatorSwitchConfirmed(operatorConfirmTarget)}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded"
                  >
                    Switch
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk "Move to Network" modal - scan on one reference radio, apply the chosen operator
          to every selected radio (see documentation/decisions.md "S380 operator selection
          capability"). Each radio keeps its own local watchdog/rollback - this only relays the
          switch request to each one, in parallel, and never waits on their individual outcomes. */}
      {bulkNetworkOpen && (
        <div data-management-modal className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-lg p-6 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-bold text-white">Move {selectedIds.size} radio(s) to a network</h3>
                <p className="text-gray-400 text-sm">Scan one radio, then apply the chosen network to all selected radios.</p>
              </div>
              <button onClick={closeBulkNetworkModal} className="text-gray-400 hover:text-white">✕</button>
            </div>

            <label className="block">
              <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Reference radio to scan</p>
              <select
                value={bulkNetworkReferenceId}
                onChange={(e) => {
                  setBulkNetworkReferenceId(e.target.value);
                  setBulkNetworkScanResult([]);
                  setBulkNetworkMessage(null);
                }}
                disabled={bulkNetworkPending !== null}
                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-sm text-white disabled:opacity-50"
              >
                {[...selectedIds].map((id) => {
                  const r = radios.find((x) => x.id === id);
                  return (
                    <option key={id} value={id}>
                      {r ? `${r.radio_id} — ${r.name}${r.status !== 'online' ? ' (offline)' : ''}` : id}
                    </option>
                  );
                })}
              </select>
            </label>

            <div className="flex items-center gap-3">
              <button
                onClick={handleBulkNetworkScan}
                disabled={
                  !bulkNetworkReferenceId ||
                  bulkNetworkPending !== null ||
                  radios.find((r) => r.id === bulkNetworkReferenceId)?.status !== 'online'
                }
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
              >
                {bulkNetworkPending === 'scan' ? 'Scanning…' : '📡 Scan networks'}
              </button>
              <button
                onClick={handleBulkNetworkAutomatic}
                disabled={bulkNetworkPending !== null}
                className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded"
              >
                {bulkNetworkPending === 'automatic' ? 'Requesting…' : `Return all ${selectedIds.size} to Automatic`}
              </button>
            </div>

            {bulkNetworkMessage && (
              <div className="flex items-center gap-2">
                {bulkNetworkPending && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>}
                <p className="text-sm text-gray-400">{bulkNetworkMessage}</p>
              </div>
            )}

            {bulkNetworkScanResult.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-gray-400 uppercase font-semibold">Available Networks</p>
                {bulkNetworkScanResult
                  .filter((op) => op.availability !== 'FORBIDDEN')
                  .map((op, idx) => (
                    <div key={`${op.mcc_mnc}-${idx}`} className="flex items-center justify-between border-t border-gray-700 pt-1">
                      <div>
                        <p className="text-white text-sm">{op.name || 'Unknown'} ({op.mcc_mnc || '?'})</p>
                        <p className="text-gray-400 text-xs">
                          {op.technology} · {op.availability === 'CURRENT' ? 'Current (on reference radio)' : 'Available'}
                        </p>
                      </div>
                      <button
                        onClick={() => setBulkNetworkConfirmTarget(op)}
                        disabled={bulkNetworkPending !== null}
                        className="text-blue-400 hover:text-blue-300 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Select
                      </button>
                    </div>
                  ))}
              </div>
            )}

            {bulkNetworkConfirmTarget && (
              <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 space-y-2">
                <p className="text-white font-semibold">
                  Switch all {selectedIds.size} selected radios to {bulkNetworkConfirmTarget.name || bulkNetworkConfirmTarget.mcc_mnc}?
                </p>
                <p className="text-sm text-gray-400">
                  Each radio switches independently and automatically returns to Automatic mode on its own if it can't
                  reach the network or the backend - never stranded.
                </p>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setBulkNetworkConfirmTarget(null)}
                    className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleBulkNetworkApply(bulkNetworkConfirmTarget)}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded"
                  >
                    Switch all {selectedIds.size}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {bulkWifiOpen && (
        <div data-management-modal className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-lg p-6 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-bold text-white">Configure Wi‑Fi on {selectedIds.size} radio(s)</h3>
                <p className="text-gray-400 text-sm">Scan one reference radio, choose a network, enter the password, then push the profile to every selected radio.</p>
              </div>
              <button onClick={closeBulkWifiModal} className="text-gray-400 hover:text-white">✕</button>
            </div>

            <div className="rounded border border-sky-700 bg-sky-950/40 px-3 py-2 text-sm text-sky-200">
              This saves the Wi‑Fi profile on each radio. It does not force an immediate switch;
              radios connect automatically when the network is in range.
            </div>

            <label className="block">
              <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Reference radio to scan</p>
              <select
                value={bulkWifiReferenceId}
                onChange={(e) => {
                  setBulkWifiReferenceId(e.target.value);
                  setBulkWifiNetworks([]);
                  setBulkWifiSelectedNetwork(null);
                  setBulkWifiPassword('');
                  setBulkWifiMessage(null);
                }}
                disabled={bulkWifiPending !== null}
                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-sm text-white disabled:opacity-50"
              >
                {[...selectedIds].map((id) => {
                  const r = radios.find((x) => x.id === id);
                  return (
                    <option key={id} value={id}>
                      {r ? `${r.radio_id} — ${r.name}${r.status !== 'online' ? ' (offline)' : ''}` : id}
                    </option>
                  );
                })}
              </select>
            </label>

            <div className="flex items-center gap-3">
              <button
                onClick={handleBulkWifiScan}
                disabled={!bulkWifiReferenceId || bulkWifiPending !== null || radios.find((r) => r.id === bulkWifiReferenceId)?.status !== 'online'}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
              >
                {bulkWifiPending === 'scan' ? 'Scanning…' : '📶 Scan networks'}
              </button>
            </div>

            {bulkWifiMessage && (
              <div className="flex items-center gap-2">
                {bulkWifiPending && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>}
                <p className="text-sm text-gray-400">{bulkWifiMessage}</p>
              </div>
            )}

            {bulkWifiNetworks.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-400 uppercase font-semibold">Available networks</p>
                {bulkWifiNetworks.map((network) => (
                  <button
                    key={`${network.ssid}-${network.bssid || ''}`}
                    type="button"
                    onClick={() => {
                      setBulkWifiSelectedNetwork(network);
                      setBulkWifiPassword('');
                    }}
                    disabled={bulkWifiPending !== null}
                    className={`w-full text-left rounded border p-3 disabled:opacity-50 ${bulkWifiSelectedNetwork?.ssid === network.ssid ? 'border-sky-500 bg-sky-500/10' : 'border-gray-700 hover:bg-gray-700/60'}`}
                  >
                    <span className="block font-semibold text-white">{network.ssid}</span>
                    <span className="block text-xs text-gray-400">{network.security} · {network.signal_dbm} dBm</span>
                  </button>
                ))}
              </div>
            )}

            {bulkWifiSelectedNetwork && (
              <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 space-y-3">
                <p className="text-white font-semibold">Use {bulkWifiSelectedNetwork.ssid}</p>
                <label className="block">
                  <span className="block text-xs text-gray-400 uppercase font-semibold mb-1">Password</span>
                  <input
                    type="password"
                    value={bulkWifiPassword}
                    onChange={(e) => setBulkWifiPassword(e.target.value)}
                    placeholder={bulkWifiSelectedNetwork.security === 'Open' ? 'No password required' : 'Enter Wi‑Fi password'}
                    className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-500"
                  />
                </label>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setBulkWifiSelectedNetwork(null)}
                    className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkWifiApply}
                    disabled={bulkWifiPending !== null || (bulkWifiSelectedNetwork.security !== 'Open' && !bulkWifiPassword)}
                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold rounded"
                  >
                    {bulkWifiPending === 'apply' ? 'Sending…' : `Send to ${selectedIds.size} radio(s)`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {wifiRadio && (
        <div data-management-modal className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h3 className="text-xl font-bold text-white">Wi-Fi</h3>
                <p className="text-gray-400 text-sm">{wifiRadio.radio_id} · {wifiRadio.name || 'Unnamed radio'}</p>
              </div>
              <button onClick={() => setWifiRadio(null)} className="text-gray-400 hover:text-white" aria-label="Close">✕</button>
            </div>
            <WifiControls radioId={wifiRadio.id} online={wifiRadio.status !== 'offline'} />
          </div>
        </div>
      )}

      {callAlertRadio && (
        <div data-management-modal className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-lg p-6 space-y-4">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h3 className="text-xl font-bold text-white">Send Call Alert</h3>
                <p className="text-gray-400 text-sm">
                  {callAlertRadio.radio_id} · {callAlertRadio.name || 'Unnamed radio'}
                </p>
              </div>
              <button onClick={closeCallAlertModal} className="text-gray-400 hover:text-white" aria-label="Close">
                ✕
              </button>
            </div>
            <label className="block">
              <span className="block text-sm font-semibold text-gray-300 mb-1">Message (optional)</span>
              <textarea
                value={callAlertMessage}
                onChange={(event) => setCallAlertMessage(event.target.value.slice(0, 500))}
                placeholder="Enter a message for the radio operator"
                rows={4}
                maxLength={500}
                className="w-full resize-y bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
              <span className="block text-right text-xs text-gray-500 mt-1">{callAlertMessage.length}/500</span>
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCallAlertModal}
                disabled={callAlertId !== null}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCallAlert}
                disabled={callAlertId !== null || callAlertRadio.status !== 'online'}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-semibold rounded"
              >
                {callAlertId !== null ? 'Sending...' : 'Send alert'}
              </button>
            </div>
          </div>
        </div>
      )}

      {(messageRadio || messageToAll) && (
        <div data-management-modal className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-lg p-6 space-y-4">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h3 className="text-xl font-bold text-white">{messageToAll ? 'Send message to all radios' : 'Send message'}</h3>
                <p className="text-gray-400 text-sm">{messageToAll ? 'All authorized radios with Messages enabled, including offline radios.' : `${messageRadio?.radio_id} · ${messageRadio?.name || 'Unnamed radio'}`}</p>
              </div>
              <button onClick={() => { if (!messageSending) { setMessageRadio(null); setMessageToAll(false); } }} className="text-gray-400 hover:text-white" aria-label="Close">✕</button>
            </div>
            <textarea
              value={messageText}
              onChange={(event) => setMessageText(event.target.value.slice(0, 2000))}
              placeholder="Skriv besked til radioen"
              rows={6}
              maxLength={2000}
              autoFocus
              className="w-full resize-y bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setMessageRadio(null); setMessageToAll(false); }} disabled={messageSending} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded">Cancel</button>
              <button type="button" onClick={handleSendDirectMessage} disabled={messageSending || !messageText.trim()} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded">
                {messageSending ? 'Sending...' : 'Send message'}
              </button>
            </div>
          </div>
        </div>
      )}

      {locationRadio && (
        <div data-management-modal className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h3 className="text-xl font-bold text-white">Last known GPS location</h3>
                <p className="text-gray-400 text-sm">
                  {locationRadio.radio_id} · {locationRadio.name || 'Unnamed radio'} · {locationRadio.status}
                </p>
              </div>
              <button onClick={closeLocationModal} className="text-gray-400 hover:text-white" aria-label="Close">
                ✕
              </button>
            </div>

            {locationLoading && (
              <div className="h-96 flex items-center justify-center text-gray-400">Loading last known location...</div>
            )}

            {!locationLoading && locationError && (
              <div className="h-32 flex items-center justify-center text-red-300">{locationError}</div>
            )}

            {!locationLoading && !locationError && !location && (
              <div className="h-32 flex items-center justify-center text-gray-400">
                No known GPS position has been reported by this radio.
              </div>
            )}

            {!locationLoading && !locationError && location && (
              <>
                <div className="h-[28rem] rounded-lg overflow-hidden border border-gray-700">
                  <Map
                    initialViewState={{ longitude: location.longitude, latitude: location.latitude, zoom: 15 }}
                    style={{ width: '100%', height: '100%' }}
                    mapStyle={lastKnownLocationMapStyle}
                    mapLib={maplibregl as any}
                  >
                    <NavigationControl position="top-left" />
                    <Marker longitude={location.longitude} latitude={location.latitude} anchor="bottom">
                      <div className="text-3xl drop-shadow">📍</div>
                    </Marker>
                  </Map>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <div className="bg-gray-900 rounded p-3">
                    <p className="text-xs text-gray-500 uppercase">GPS fix time</p>
                    <p className="text-gray-200">{formatGpsFixTime(location.fix_time, formatLastSeen)}</p>
                  </div>
                  <div className="bg-gray-900 rounded p-3">
                    <p className="text-xs text-gray-500 uppercase">Last online</p>
                    <p className="text-gray-200">{formatLastSeen(locationRadio.last_seen)}</p>
                  </div>
                  <div className="bg-gray-900 rounded p-3">
                    <p className="text-xs text-gray-500 uppercase">Coordinates</p>
                    <p className="text-gray-200 font-mono">{location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}</p>
                  </div>
                  <div className="bg-gray-900 rounded p-3">
                    <p className="text-xs text-gray-500 uppercase">Source / accuracy</p>
                    <p className="text-gray-200">{location.source || 'Unknown'}{location.accuracy != null ? ` · ±${Math.round(location.accuracy)} m` : ''}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
